import { Routes } from '@angular/router';
import { IsAccountValidGuard } from '@app/core/guards/base/is-account-valid.guard';
import { SigninCanActivateGuard } from '@app/core/guards/signin.can-activate.guard';
import { SubscriptionUpdateGuard } from '@app/core/guards/subscription-update.guard';
import { SwitchLanguageGuard } from '@app/core/guards/switch-language.guard';
import { DeviceDetectorService } from 'ngx-device-detector';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { FilesExpiredComponent } from './files-expired/files-expired.component';
import { ServiceUnavailableComponent } from './service-unavailable/service-unavailable.component';
import { InvalidConfigurationComponent } from '@app/client/invalid-configuration/invalid-configuration.component';
import { DeletedComponent } from './deleted/deleted.component';
import { SuspendedComponent } from './suspended/suspended.component';
import { UnsupportedComponent } from './unsupported/unsupported.component';
import { UsageExceededComponent } from '@app/client/usage-exceeded/usage-exceeded.component';
import { ViewerGuard } from '@app/core/guards/viewer.guard';
import { PaypalGuard } from '@app/core/guards/paypal.guard';
import { RedirectSsoGuard } from '@app/core/guards/redirect-sso.guard';
import { RequestExpiredComponent } from './request-expired/request-expired.component';
import { ReportGuard } from '@app/core/guards/report.guard';
import { notFoundGuard } from '@app/core/guards/not-found.guard';

export function routesFactory(deviceDetector: DeviceDetectorService): Routes {
    return [
        {
            path: 'service-unavailable',
            pathMatch: 'full',
            component: ServiceUnavailableComponent,
        },
        {
            path: 'invalid-configuration',
            pathMatch: 'full',
            component: InvalidConfigurationComponent,
        },
        {
            path: 'quota-exceeded',
            pathMatch: 'full',
            component: UsageExceededComponent,
        },
        {
            path: 'deleted',
            pathMatch: 'full',
            component: DeletedComponent,
        },
        {
            path: 'unsupported',
            pathMatch: 'full',
            component: UnsupportedComponent,
        },
        {
            path: 'request-expired',
            pathMatch: 'full',
            component: RequestExpiredComponent,
        },
        {
            path: 'report',
            canActivate: [ReportGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/report/report.module').then(m => m.ReportModule),
        },
        {
            path: 'suspended',
            pathMatch: 'full',
            component: SuspendedComponent,
        },
        {
            path: 'fr',
            canActivate: [SwitchLanguageGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/language/language.module').then(m => m.LanguageModule),
        },
        {
            path: 'en',
            canActivate: [SwitchLanguageGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/language/language.module').then(m => m.LanguageModule),
        },
        {
            path: 'es',
            canActivate: [SwitchLanguageGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/language/language.module').then(m => m.LanguageModule),
        },
        {
            path: 'it',
            canActivate: [SwitchLanguageGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/language/language.module').then(m => m.LanguageModule),
        },
        {
            path: 'pt',
            canActivate: [SwitchLanguageGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/language/language.module').then(m => m.LanguageModule),
        },
        {
            path: 'de',
            canActivate: [SwitchLanguageGuard],
            pathMatch: 'prefix',
            loadChildren: () => import('@app/client/language/language.module').then(m => m.LanguageModule),
        },
        {
            path: '404',
            component: PageNotFoundComponent,
        },
        {
            path: 'expired',
            component: FilesExpiredComponent,
        },
        {
            path: 'subscription/update',
            canActivate: [SubscriptionUpdateGuard],
            loadChildren: () => import('@app/client/subscription-update/subscription-update.module').then(m => m.SubscriptionUpdateModule),
        },
        {
            path: 'account',
            loadChildren: () => {
                return deviceDetector.isMobile() ?
                    import('@app/client/account-mobile/account-mobile.module').then(m => m.AccountMobileModule) :
                    import('@app/client/account/account.module').then(m => m.AccountModule);
            },
        },
        {
            path: 'signup',
            loadChildren: () => import('@app/client/signup/signup.module').then(m => m.SignupModule),
        },
        {
            path: '',
            loadChildren: () => {
                return deviceDetector.isMobile() ?
                    import('@app/client/uploader/mobile/uploader-mobile.module').then(m => m.UploaderMobileModule) :
                    import('@app/client/uploader/desktop/uploader.module').then(m => m.UploaderModule);
            },
        },
        {
            path: 'redirect-sso',
            canActivate: [RedirectSsoGuard],
            loadChildren: () => import('@app/client/redirect-sso/redirect-sso.module').then(m => m.RedirectSsoModule),
        },
        {
            path: 'redirect',
            canActivate: [IsAccountValidGuard],
            loadChildren: () => import('@app/client/redirect/redirect.module').then(m => m.RedirectModule),
        },
        {
            path: 'signin',
            canActivate: [SigninCanActivateGuard],
            loadChildren: () => {
                return deviceDetector.isMobile() ?
                    import('@app/client/signin/mobile/signin-mobile.module').then(m => m.SigninMobileModule) :
                    import('@app/client/signin/desktop/signin.module').then(m => m.SigninModule);
            },
        },
        {
            path: 'signout',
            loadChildren: () => import('@app/client/signout/signout.module').then(m => m.SignoutModule),
        },
        {
            path: 'paypal',
            canActivate: [PaypalGuard],
            loadChildren: () => import('@app/client/paypal/paypal.module').then(m => m.PaypalModule),
        },
        {
            path: ':id',
            canActivate: [ViewerGuard],
            data: { moduleName: 'ViewerModule' },
            loadChildren: () => import('@app/client/viewer/viewer.module').then(m => m.ViewerModule),
        },
        {
            path: '**',
            canActivate: [notFoundGuard],
            component: PageNotFoundComponent,
        }
    ];
}
